import React, { memo, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import {
  Box,
  Text,
  Button,
  Checkbox,
  KeyboardShift,
  Loader,
} from '@bonanza-social/moonlight';
import {
  useAttestationForm,
  useUserDetails,
  useUserFullAddress,
  useValidateManualFields,
} from '../../hooks';
import { selectIsPepSettedByPlayer } from '@bonanza-social/account/state';
import { AttestationSectionProps } from './AttestationSection.types';
import { getCountryCodeByPhoneCode } from '../../utils';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useTranslation } from '@bonanza-social/i18n';
import { BottomSheet, SelectableOptionItem } from '@bonanza-social/navigation';
import AddressField from '../AddressField/AddressField';
import ManualFieldInput from '../ManualFieldInput/ManualFieldInput';
import { AddressInfo } from '../AddressField/AddressField.types';
import { Pressable, TextInput } from 'react-native';
import {
  reassessAccount,
  selectReassessmentDataState,
} from '../../state/detailsAttestation.slice';
import { formatAddressLine1 } from '../../utils';
import { USER_NAME_FIELDS } from './AttestationSection.content';

function AttestationSection({ onClose }: AttestationSectionProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('detailsAttestation');
  const inputRefs = useRef<(TextInput | null)[]>([]);
  const phoneNumberRef = useRef<TextInput | null>(null);

  const { userDetails } = useUserDetails();
  const { fullAddress, isAddressLoaded } = useUserFullAddress();
  const reassessmentDataState = useSelector(selectReassessmentDataState);
  const isPepSettedByPlayer = useSelector(selectIsPepSettedByPlayer);

  const {
    userInfo,
    addressInfo,
    addressInfoErrors,
    occupations,
    phoneCodeList,
    phoneNumberError,
    isPep,
    focusFirstInputField,
    selectedImageSource,
    setIsPep,
    setFocusFirstInputField,
    setSelectedImageSource,
    setAddressInfo,
    setAddressInfoErrors,
    setPhoneNumberError,
    setUserInfo,
    handleUserInfoFieldChange,
  } = useAttestationForm();

  const validationRules = useMemo(
    () => ({
      firstName: {
        regex: /^[A-Za-z .'\\-]+$/,
        errorKey: 'invalidCharacters',
      },
      lastName: {
        regex: /^[A-Za-z .'\\-]+$/,
        errorKey: 'invalidCharacters',
      },
    }),
    [],
  );

  const { errors, validateManualFields } = useValidateManualFields(
    userInfo,
    t,
    ['phoneCode', 'phoneNumber', 'address'],
    validationRules,
  );

  useEffect(() => {
    if (userDetails) {
      setUserInfo((prev) => ({
        ...prev,
        firstName: userDetails.firstName || '',
        lastName: userDetails.lastName || '',
        phoneCode: userDetails.phoneCode || '',
        phoneNumber: userDetails.phoneNumber || '',
        occupation: userDetails.occupation || '',
      }));
    }

    setIsPep(!isPepSettedByPlayer);
  }, [userDetails, fullAddress, isPepSettedByPlayer, setUserInfo, setIsPep]);

  const imageSource = useMemo(
    () =>
      `https://flagcdn.com/w20/${getCountryCodeByPhoneCode(userInfo.phoneCode)}.png`,
    [userInfo.phoneCode],
  );

  const addressLine1 = useMemo(() => {
    return formatAddressLine1(
      addressInfo.unit,
      addressInfo.building,
      addressInfo.street,
    );
  }, [addressInfo.unit, addressInfo.building, addressInfo.street]);

  const sectionIsValid = useMemo(() => {
    const errorsAreValid = Object.values(errors).every((error) => error === '');

    const addressErrorsAreValid = Object.values(addressInfoErrors).every(
      (error) => error === '',
    );

    return (
      errorsAreValid && addressErrorsAreValid && phoneNumberError.length === 0
    );
  }, [errors, addressInfoErrors, phoneNumberError]);

  useEffect(() => {
    const countryCode = getCountryCodeByPhoneCode(
      userInfo.phoneCode,
    ) as CountryCode;
    const parsedNumber = parsePhoneNumberFromString(
      `${userInfo.phoneCode}${userInfo.phoneNumber}`,
      countryCode,
    );
    const isNumberValid = Boolean(parsedNumber && parsedNumber.isValid());

    setPhoneNumberError(isNumberValid ? '' : t('invalidPhoneNumber'));
  }, [userInfo.phoneCode, userInfo.phoneNumber, setPhoneNumberError, t]);

  useEffect(() => {
    validateManualFields();
  }, [validateManualFields]);

  const handleSubmitEditing = useCallback(
    (currentIndex: number) => {
      if (currentIndex < USER_NAME_FIELDS.length - 1) {
        inputRefs.current[currentIndex + 1]?.focus();
      }
      if (currentIndex === USER_NAME_FIELDS.length - 1) {
        setFocusFirstInputField(true);
      }
    },
    [setFocusFirstInputField],
  );

  const handleAddressInfoChange = useCallback(
    (updatedAddressInfo: Partial<AddressInfo>, errors: AddressInfo) => {
      setAddressInfo((prevInfo) => ({
        ...prevInfo,
        ...updatedAddressInfo,
      }));
      setAddressInfoErrors(errors);
    },
    [setAddressInfo, setAddressInfoErrors],
  );

  const onPhoneCodePressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'InputSelect',
      data: {
        selectableOptions: phoneCodeList,
        headerNS: 'detailsAttestation',
        headerKey: 'phoneCode',
        buttonNS: 'detailsAttestation',
        buttonKey: 'saveButton',
        centerContent: true,
        allowEmptyField: false,
        maxHeight: '30%',
        defaultValue: userInfo.phoneCode,
        onSelect: (selectedValue: SelectableOptionItem) => {
          setSelectedImageSource(selectedValue?.image?.source);
          setUserInfo((prev) => ({
            ...prev,
            phoneCode: selectedValue?.text.split(' ')[0],
          }));
          phoneNumberRef.current?.focus();
        },
      },
    });
  }, [
    phoneCodeList,
    setUserInfo,
    setSelectedImageSource,
    phoneNumberRef,
    userInfo.phoneCode,
  ]);

  const onOccupationPressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'InputSelect',
      data: {
        selectableOptions: occupations,
        headerNS: 'detailsAttestation',
        headerKey: 'occupation',
        buttonNS: 'detailsAttestation',
        buttonKey: 'saveButton',
        centerContent: true,
        maxHeight: '30%',
        defaultValue: userInfo.occupation,
        onSelect: (selectedValue: SelectableOptionItem) => {
          setUserInfo((prev) => ({
            ...prev,
            occupation: selectedValue?.text,
          }));
        },
      },
    });
  }, [occupations, userInfo.occupation, setUserInfo]);

  const handleSaveDetails = useCallback(async () => {
    if (!sectionIsValid) return;
    await dispatch(
      reassessAccount({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        address: addressLine1 ?? userInfo.address,
        building: addressInfo.building ?? fullAddress?.BuildingNumber,
        unit: addressInfo.unit ?? fullAddress?.SubBuilding,
        postalCode: addressInfo.postalCode ?? fullAddress?.PostalCode,
        city: addressInfo.city ?? fullAddress?.City,
        street: addressInfo.street ?? fullAddress?.Street,
        occupation: userInfo.occupation,
        provinceCode: 'ON',
        country: userDetails.country,
        countryCode: userDetails.countryCode,
        phoneCode: userInfo.phoneCode,
        phoneNumber: userInfo.phoneNumber,
        email: userDetails.email,
        pepCheck: isPep,
      }),
    );

    requestAnimationFrame(() => {
      onClose();
    });
  }, [
    dispatch,
    addressLine1,
    sectionIsValid,
    fullAddress,
    userDetails,
    userInfo,
    addressInfo,
    onClose,
    isPep,
  ]);

  if (!userDetails) {
    return null;
  }

  if (!isAddressLoaded) {
    return (
      <Box absoluteFill centerContent>
        <Loader />
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <KeyboardShift showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
        <Box>
          <Text
            variant="h4"
            color="darkSurfacePrimary"
            locNS="detailsAttestation"
            locKey="personalDetailsConfirmation"
            marginBottom="space-400"
            paddingTop="space-300"
            marginTop="safe-area-inset-top"
          />
        </Box>
        <Text
          variant="body-m-semi"
          color="darkSurfacePrimary"
          locNS="detailsAttestation"
          locKey="attestationDescription"
          marginBottom="space-600"
        />
        <Box gap="space-400">
          {USER_NAME_FIELDS.map(({ field, placeholderKey }, index) => (
            <ManualFieldInput
              key={field}
              ref={(el) => (inputRefs.current[index] = el)}
              field={field}
              value={userInfo[field as keyof typeof userInfo]}
              enterKeyHint="next"
              onSubmitEditing={() => handleSubmitEditing(index)}
              placeholderNS="detailsAttestation"
              placeholderKey={placeholderKey}
              submitBehavior={
                index === USER_NAME_FIELDS.length - 1
                  ? 'blurAndSubmit'
                  : 'submit'
              }
              error={errors[field]}
              onChange={handleUserInfoFieldChange}
            />
          ))}
          <AddressField
            fullAddress={fullAddress}
            defaultAddress={userDetails.addressLine1}
            focusFirstInputField={focusFirstInputField}
            setFocusFirstInputField={setFocusFirstInputField}
            province={userDetails.province}
            onAddressInfoChange={handleAddressInfoChange}
          />
          <Pressable onPress={onOccupationPressIn}>
            <ManualFieldInput
              field="occupation"
              editable={false}
              value={userInfo.occupation}
              enterKeyHint="next"
              placeholderNS="detailsAttestation"
              placeholderKey="occupation"
              error={errors.occupation}
              onChange={handleUserInfoFieldChange}
              onPressIn={onOccupationPressIn}
            />
          </Pressable>
          <Box flexDirection="row" gap="space-200" alignItems="center">
            <Box flex={1}>
              <Pressable onPress={onPhoneCodePressIn}>
                <ManualFieldInput
                  field="phoneCode"
                  image={{
                    source: selectedImageSource || imageSource,
                    width: 20,
                  }}
                  editable={false}
                  value={userInfo.phoneCode}
                  placeholderNS="detailsAttestation"
                  placeholderKey="phoneCode"
                  error={phoneNumberError}
                  showError={false}
                  onChange={handleUserInfoFieldChange}
                  onPressIn={onPhoneCodePressIn}
                />
              </Pressable>
            </Box>
            <Box flex={2}>
              <ManualFieldInput
                ref={phoneNumberRef}
                field="phoneNumber"
                value={userInfo.phoneNumber}
                enterKeyHint="go"
                placeholderNS="detailsAttestation"
                placeholderKey="phoneNumber"
                keyboardType="number-pad"
                error={phoneNumberError}
                showError={false}
                onChange={handleUserInfoFieldChange}
              />
            </Box>
          </Box>
          {phoneNumberError ? (
            <Text color="darkSurfaceError" variant="body-m">
              {phoneNumberError}
            </Text>
          ) : null}
          <Checkbox
            labelKey="pepLabel"
            labelNS="detailsAttestation"
            checked={isPep}
            onChange={setIsPep}
          ></Checkbox>
        </Box>
      </KeyboardShift>
      <Box paddingBottom="space-800">
        <Button
          variant="primary"
          disabled={!sectionIsValid}
          size="large"
          locNS="detailsAttestation"
          locKey="saveDetails"
          onPress={handleSaveDetails}
          loading={reassessmentDataState === 'pending'}
        />
      </Box>
    </Box>
  );
}

export default memo(AttestationSection);
